/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Target, Eye, Heart, Star } from 'lucide-react';

const AboutPage = ({ onSignUp }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white pt-16">
      {/* Hero Section with Photo */}
      <div className="relative h-[600px] bg-black">
        <img
          src="https://customer-assets.emergentagent.com/job_07e03d84-96d6-4787-9cf6-a4abd4f4b8fa/artifacts/in9ki500_IMG_5137.JPEG"
          alt="Pastor Obed Ansu"
          className="w-full h-full object-cover opacity-70"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-12 max-w-7xl mx-auto">
          <h1 className="text-5xl font-light text-white mb-4">
            About Pastor Obed Ansu
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            Raising a generation of reformers who carry heaven's agenda.
          </p>
        </div>
      </div>

      {/* Biography Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_07e03d84-96d6-4787-9cf6-a4abd4f4b8fa/artifacts/2o69s7a2_IMG_6658.JPG"
              alt="Pastor Obed Ansu Ministry"
              className="rounded-2xl shadow-2xl"
            />
          </div>
          <div>
            <h2 className="text-4xl font-light text-gray-900 mb-6">
              The Journey
            </h2>
            <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
              <p>
                <strong>Pastor Obed Ansu</strong> is a prophetic and apostolic voice to this generation, the pastor of <strong>Vine City Church</strong> and founder of <strong>More Youth</strong>. Called to raise a generation of consecrated leaders, world changers, and kingdom pioneers, he carries a passion to see young people transformed by the power of God and released into every sphere of influence.
              </p>
              <p>
                As a mentor and spiritual father to many, Pastor Obed is committed to building leaders through prophetic revelation, apostolic doctrine, and spiritual formation. His ministry is marked by a strong emphasis on intimacy with God, discipleship, and the raising of prophetic reformers who advance heaven's agenda on earth.
              </p>
              <p>
                Through his mentorship programs, he equips both emerging leaders and prophetic minstrels/musicians, imparting truth, activating spiritual gifts, and training worshippers to become carriers of God's glory. With a unique gift for both the Word and worship, he personally teaches and mentors young musicians, including offering hands-on bass lessons as part of raising the next generation of kingdom minstrels.
              </p>
              <p>
                Pastor Obed is the convener of the <strong>Here Come the Sent Ones Conference</strong>, an annual gathering that equips believers prophetically and apostolically to become militant soldiers of the Kingdom. He serves under the spiritual covering of Pastor Randolph Adjei, who has been a spiritual father and mentor to him.
              </p>
              <p>
                Driven by a burden for revival and reformation, Pastor Obed Ansu's heartbeat is to see the earth filled with the knowledge of the glory of the Lord as the waters cover the sea (<em>Habakkuk 2:14</em>).
              </p>
            </div>
          </div>
        </div>

        {/* Vision & Mission */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <div className="bg-gray-50 rounded-2xl p-10">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
              <Eye className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Our Vision</h3>
            <p className="text-gray-700 text-lg leading-relaxed">
              To raise a generation of apostolic and prophetic leaders who are trained, transformed, and sent to impact nations with the Kingdom of God. We envision believers who are not just followers, but leaders who carry divine authority and manifest heaven's agenda on earth.
            </p>
          </div>

          <div className="bg-gray-50 rounded-2xl p-10">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
              <Target className="w-8 h-8 text-orange-600" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Our Mission</h3>
            <p className="text-gray-700 text-lg leading-relaxed">
              To provide comprehensive spiritual training and mentorship that equips believers with biblical knowledge, prophetic insight, leadership skills, and worship excellence. We are committed to developing well-rounded Kingdom leaders through transformative teaching and practical application.
            </p>
          </div>
        </div>

        {/* Quote Banner */}
        <div className="bg-black rounded-2xl p-16 text-center mb-20">
          <blockquote className="text-3xl font-light text-white mb-6">
            "We raise a generation of reformers who carry heaven's agenda."
          </blockquote>
          <p className="text-gray-400 text-lg">- Pastor Obed Ansu</p>
        </div>

        {/* Testimonials Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-purple-50 rounded-full px-4 py-2 mb-4">
              <Star className="w-4 h-4 text-purple-600" />
              <span className="text-purple-600 text-sm font-semibold">Student Testimonials</span>
            </div>
            <h2 className="text-4xl font-light text-gray-900 mb-4">Transformed Lives</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Here's what students are saying after committing to 12 months of discipleship
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Testimonial 1 - Samuel Anderson */}
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="flex text-orange-500">
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                </div>
              </div>
              <p className="text-gray-700 mb-4 italic leading-relaxed">
                "I would absolutely recommend this mentorship. It is a privilege to be mentored under pastor Obed. He is a great teacher AND friend. It helps you build resilience in the Word of God. If you desire to know God deeply but don't know where to start from, I strongly recommend joining this mentorship."
              </p>
              <div className="border-t border-gray-300 pt-4">
                <p className="font-semibold text-gray-900">Samuel Anderson</p>
                <p className="text-sm text-gray-500">Academy Student</p>
              </div>
            </div>

            {/* Testimonial 2 - Jeremiah */}
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="flex text-orange-500">
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                </div>
                <span className="ml-2 text-sm font-semibold text-gray-700">10/10</span>
              </div>
              <p className="text-gray-700 mb-4 italic leading-relaxed">
                "The most valuable insights I had was in how to work with God better, in a new dimension and deepness. It has forced me to take my walk with Christ more seriously, and has set me up well to continue on that path and go even deeper with Him."
              </p>
              <div className="border-t border-gray-300 pt-4">
                <p className="font-semibold text-gray-900">Jeremiah Dom-Ogbonna</p>
                <p className="text-sm text-gray-500">Academy Student</p>
              </div>
            </div>

            {/* Testimonial 3 - Chantelle */}
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="flex text-orange-500">
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4" />
                </div>
                <span className="ml-2 text-sm font-semibold text-gray-700">8/10</span>
              </div>
              <p className="text-gray-700 mb-4 italic leading-relaxed">
                "The word of God is ALIVE! My mentor made me understand how important the Word is and that alone has changed my life because I was taking the Word for granted. This has changed my perspective. I am on a journey of building discipline."
              </p>
              <div className="border-t border-gray-300 pt-4">
                <p className="font-semibold text-gray-900">Chantelle Asare</p>
                <p className="text-sm text-gray-500">Academy Student</p>
              </div>
            </div>

            {/* Testimonial 4 - Debby Brown */}
            <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="flex items-center mb-4">
                <div className="flex text-orange-500">
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                  <Star className="w-4 h-4 fill-current" />
                </div>
                <span className="ml-2 text-sm font-semibold text-gray-700">10/10</span>
              </div>
              <p className="text-gray-700 mb-4 italic leading-relaxed">
                "Hearing the voice of God clearly. Knowing there are different spirit smells. Impacted greatly. Making sure I'm in alignment with the plan of God, by hearing the voice of God. Yes, definitely! Because it helps you establish a personal relationship and grow."
              </p>
              <div className="border-t border-gray-300 pt-4">
                <p className="font-semibold text-gray-900">Debby Brown</p>
                <p className="text-sm text-gray-500">Academy Student</p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center">
          <h3 className="text-3xl font-light text-gray-900 mb-6">
            Ready to Begin Your Journey?
          </h3>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of believers who are being trained and transformed through apostolic and prophetic mentorship.
          </p>
          <button
            onClick={() => onSignUp ? onSignUp() : navigate('/signup')}
            className="bg-purple-600 hover:bg-purple-700 text-white px-12 py-4 rounded-full text-lg font-medium transition-colors inline-flex items-center"
          >
            Join Academy
            <ChevronRight className="w-5 h-5 ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;